// frontend/src/components/Debug/AuthDebuggerClean.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const AuthDebuggerClean = () => {
  // All hooks must be called at the top level
  const [expanded, setExpanded] = useState(false);
  const [authLogs, setAuthLogs] = useState([]);
  const [requestCount, setRequestCount] = useState(0);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    loading: true,
    hasToken: false,
    hasRefreshToken: false
  });

  // Monitor localStorage for auth state changes
  useEffect(() => {
    const updateAuthState = () => {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      setAuthState({
        isAuthenticated: !!token,
        loading: false,
        hasToken: !!token,
        hasRefreshToken: !!refreshToken
      });
    };

    // Initial check
    updateAuthState();

    // Listen for storage changes
    const handleStorageChange = () => updateAuthState();
    window.addEventListener('storage', handleStorageChange);
    
    // Poll for changes (in case of same-tab updates)
    const interval = setInterval(updateAuthState, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Monitor network requests
  useEffect(() => {
    const originalFetch = window.fetch;
    
    const monitoredFetch = (...args) => {
      const url = args[0];
      
      // Only monitor auth-related requests
      if (typeof url === 'string' && (url.includes('/auth/') || url.includes('/api/'))) {
        setRequestCount(prev => prev + 1);
        
        const logEntry = {
          id: Date.now() + Math.random(),
          timestamp: new Date().toLocaleTimeString(),
          type: 'API_REQUEST',
          url: url.replace(window.location.origin, ''),
          method: args[1]?.method || 'GET',
          hasAuth: !!(args[1]?.headers?.Authorization || args[1]?.headers?.authorization)
        };
        
        setAuthLogs(prev => [logEntry, ...prev.slice(0, 19)]); // Keep last 20 logs
      }
      
      return originalFetch.apply(window, args);
    };

    window.fetch = monitoredFetch;

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const clearLogs = () => {
    setAuthLogs([]);
    setRequestCount(0);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.reload();
  };

  const getStatusColor = () => {
    if (authState.loading) return 'warning';
    if (authState.isAuthenticated) return 'success';
    return 'error';
  };

  const getStatusText = () => {
    if (authState.loading) return 'Loading';
    if (authState.isAuthenticated) return 'Auth';
    return 'No Auth';
  };

  // Early return after all hooks have been called
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: expanded ? 350 : 160,
        maxHeight: expanded ? 400 : 50,
        zIndex: 9999,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        opacity: 0.95
      }}
      elevation={8}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: 'primary.main',
          color: 'white',
          minHeight: 40
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" fontWeight="bold">
            Auth
          </Typography>
          <Chip
            label={getStatusText()}
            size="small"
            color={getStatusColor()}
            variant="outlined"
            sx={{ 
              color: 'white', 
              borderColor: 'white', 
              height: 20,
              fontSize: '0.7rem'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ mr: 1 }}>
            {requestCount}
          </Typography>
          <IconButton size="small" sx={{ color: 'white', p: 0.5 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Auth State */}
          <Typography variant="subtitle2" gutterBottom>
            State
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" display="block">
              Token: {authState.hasToken ? '✅' : '❌'}
            </Typography>
            <Typography variant="caption" display="block">
              Refresh: {authState.hasRefreshToken ? '✅' : '❌'}
            </Typography>
            <Typography variant="caption" display="block">
              Status: {authState.isAuthenticated ? '✅ Auth' : '❌ No Auth'}
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearLogs}
              sx={{ fontSize: '0.7rem' }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleLogout}
              sx={{ fontSize: '0.7rem' }}
            >
              Logout
            </Button>
          </Box>

          {/* Request Log */}
          <Typography variant="subtitle2" gutterBottom>
            Requests ({authLogs.length})
          </Typography>
          <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
            {authLogs.slice(0, 8).map((log) => (
              <ListItem key={log.id} sx={{ py: 0.25, px: 0 }}>
                <ListItemText
                  primary={
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {log.method} {log.url.split('/').pop()}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {log.timestamp} {log.hasAuth ? '🔐' : '🔓'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
            {authLogs.length === 0 && (
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText
                  primary={
                    <Typography variant="caption" color="text.secondary">
                      No requests yet
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AuthDebuggerClean;
